import {
	Component,
	OnInit,
	ChangeDetectionStrategy,
	Input,
	Output,
	EventEmitter,
} from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import {
	MatSlideToggleChange,
	MatSlideToggleModule,
} from '@angular/material/slide-toggle';
import { ICmmsScheduleValue } from '@app/pages/cmms/interfaces/scheduled-value.interface';
import { ScheduleOptionsComponent } from './schedule-options/schedule-options.component';

import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { NgxTolgeeModule } from '@tolgee/ngx';
import { IWorkOrderRoutine } from '@app/domain/cmms-types/workorder-types';

export function ValidateWeekSchedule(control: AbstractControl) {
	if (control.value.period === 'WEEK' && !control.value.selectedDays.length) {
		return { invalidDaysOfWeek: true };
	}

	return null;
}

@Component({
	selector: 'cmms-schedule-section',
	templateUrl: './cmms-schedule-section.component.html',
	styleUrls: ['./cmms-schedule-section.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		MatSlideToggleModule,
		ScheduleOptionsComponent,
		MatTooltipModule,
		MatIconModule,
		NgxTolgeeModule,
	],
})
export class CmmsScheduleSectionComponent implements OnInit {
	@Input() set routineData(routineData: IWorkOrderRoutine) {
		if (!routineData) {
			this.patchScheduleControl({
				scheduled: false,
			});
			return;
		}
		this.patchScheduleControl({
			scheduled: true,
			...routineData,
		});
	}

	@Input() useDebounce: boolean;

	@Input() actionCenter: boolean;

	@Output() routineDataChanged = new EventEmitter<{ routineData: IWorkOrderRoutine }>();
	@Output() actionCenterRoutineDataChanged = new EventEmitter<IWorkOrderRoutine[]>();
	@Output()
	controlReady = new EventEmitter<FormControl<ICmmsScheduleValue>>();

	formControl = new FormControl<ICmmsScheduleValue>(
		{
			scheduled: false,
			period: 'DAY',
			interval: 1,
			excludeWeekends: false,
			selectedDays: ['monday'],
			monthSetup: 'FIRST_DAY',
		},
		ValidateWeekSchedule
	);

	ngOnInit(): void {
		this.controlReady.emit(this.formControl);
		if (this.actionCenter) {
			this.actionCenterRoutineDataChanged.emit([this.getRoutineVariables()]);
		}
	}

	toggleSchedule(evt: MatSlideToggleChange) {
		this.patchScheduleControl({ scheduled: evt.checked });
		this.emitRoutineChange();
	}

	scheduleOptionsChanged(data: Partial<ICmmsScheduleValue>) {
		this.patchScheduleControl(data);
		this.emitRoutineChange();
	}

	patchScheduleControl(data: Partial<ICmmsScheduleValue>): void {
		this.formControl.patchValue(
			{
				scheduled: this.checkAndSetValueByKey('scheduled', data),
				period: this.checkAndSetValueByKey('period', data),
				interval: this.checkAndSetValueByKey('interval', data),
				excludeWeekends: this.checkAndSetValueByKey('excludeWeekends', data),
				selectedDays: this.checkAndSetValueByKey('selectedDays', data),
				monthSetup: this.checkAndSetValueByKey('monthSetup', data),
			},
			{ emitEvent: false }
		);
	}

	private checkAndSetValueByKey(key: string, value: any): any {
		return this.isTrueValue(value[key]) ? value[key] : this.formControlValue[key];
	}

	private emitRoutineChange(): void {
		if (this.actionCenter) {
			this.actionCenterRoutineDataChanged.emit([this.getRoutineVariables()]);
			return;
		}
		this.routineDataChanged.emit({ routineData: this.getRoutineVariables() });
	}

	private getRoutineVariables(): IWorkOrderRoutine {
		const periodValuesMap = {
			DAY: {
				excludeWeekends: this.formControlValue.excludeWeekends,
			},
			WEEK: {
				selectedDays: this.formControlValue.selectedDays,
			},
			MONTH: {
				monthSetup: this.formControlValue.monthSetup,
			},
			YEAR: {}, // Leave it here as in future we may have Year choices
		};

		const routinePayload = {
			period: this.formControlValue.period,
			interval: this.formControlValue.interval,
			...periodValuesMap[this.formControlValue.period],
		};

		return this.formControlValue.scheduled ? routinePayload : null;
	}

	private isTrueValue(value: any): boolean {
		return value !== undefined && value !== null;
	}

	get formControlValue(): ICmmsScheduleValue {
		return this.formControl.value;
	}

	get isInvalidDaysOfWeek(): boolean {
		return this.formControl.hasError('invalidDaysOfWeek');
	}
}
